import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import {
  Card,
  CodeBlock,
  CopyButton,
  ErrorNote,
  Eyebrow,
  Field,
  Kpi,
  PageHead,
  Spinner,
  Status,
} from '../../components/ui'
import { api, wsPath } from '../../lib/api'
import { fmtDate, fmtMoney } from '../../lib/format'
import { useT } from '../../lib/i18n'
import { ConnectedApps } from './ConnectedApps'
import { GroupsTab } from './Groups'
import { useRole, useWs } from './useWs'

const TABS = [
  'program',
  'groups',
  'integrations',
  'coupons',
  'webhooks',
  'domains',
  'apiKeys',
  'team',
  'billing',
  'audit',
] as const
type Tab = (typeof TABS)[number]
const DOMAIN_ERRORS = [
  'txt_not_found',
  'cname_not_target',
  'cloudflare_for_saas_not_configured',
  'dns_missing_7_days',
] as const

export function SettingsPage() {
  const { t } = useT()
  const initial = (new URLSearchParams(location.search).get('tab') as Tab) ?? 'program'
  const [tab, setTab] = useState<Tab>(TABS.includes(initial) ? initial : 'program')
  return (
    <div>
      <PageHead title={t('settings')} />
      <div className="mb-6 flex flex-wrap gap-1 border-b border-hairline pb-3" role="tablist">
        {TABS.map((x) => (
          <button
            key={x}
            type="button"
            role="tab"
            aria-selected={tab === x}
            className={`tab ${tab === x ? 'on' : ''}`}
            onClick={() => {
              setTab(x)
              history.replaceState(null, '', `?tab=${x}`)
            }}
          >
            {t(x)}
          </button>
        ))}
      </div>
      {tab === 'program' && <ProgramTab />}
      {tab === 'groups' && <GroupsTab />}
      {tab === 'integrations' && <IntegrationsTab />}
      {tab === 'domains' && <DomainsTab />}
      {tab === 'apiKeys' && (
        <>
          <ApiKeysTab />
          <ConnectedApps />
        </>
      )}
      {tab === 'team' && <TeamTab />}
      {tab === 'billing' && <BillingTab />}
      {tab === 'coupons' && <CouponsTab />}
      {tab === 'webhooks' && <WebhooksTab />}
      {tab === 'audit' && <AuditTab />}
    </div>
  )
}

interface ProgramData {
  program: {
    id: string
    name: string
    website: string
    description: string | null
    autoApprove: boolean
    published: boolean
    currency: string
    slug: string
    termsVersion: number
    brandColor: string | null
  }
  terms: {
    rewards: { event: 'sale' | 'lead'; type: 'percent' | 'fixed'; bps?: number; amountMinor?: number }[]
    holdDays: number
    minPayoutMinor: number
    allowedHosts: string[]
  }
  versions: { version: number; createdAt: string }[]
  standards: { rewardDurationMonths: number; cookieDays: number }
}

function ProgramTab() {
  const { t, locale } = useT()
  const ws = useWs()
  const role = useRole()
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['program', ws], queryFn: () => api<ProgramData>(wsPath(ws, '/program')) })
  const [p, setP] = useState<Partial<ProgramData['program']> | null>(null)
  const [terms, setTerms] = useState<ProgramData['terms'] | null>(null)
  const save = useMutation({
    mutationFn: () =>
      api(wsPath(ws, '/program'), {
        method: 'PATCH',
        body: {
          name: p!.name,
          description: p!.description ?? null,
          website: p!.website,
          autoApprove: p!.autoApprove,
          published: p!.published,
          brandColor: p!.brandColor ?? null,
        },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['program', ws] }),
  })
  const saveTerms = useMutation({
    mutationFn: () => api(wsPath(ws, '/program/terms'), { method: 'PUT', body: terms }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['program', ws] })
      setTerms(null)
    },
  })
  if (!q.data) return <Spinner />
  const cur = p ?? q.data.program
  const tm = terms ?? q.data.terms
  const reward = tm.rewards[0]!
  const editable = role === 'owner' || role === 'admin'
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <Eyebrow className="mb-6">{t('program')}</Eyebrow>
        <form
          className="grid gap-5"
          onSubmit={(e) => {
            e.preventDefault()
            save.mutate()
          }}
        >
          <Field label={t('programName')}>
            <input
              className="input"
              disabled={!editable}
              value={cur.name ?? ''}
              onChange={(e) => setP({ ...cur, name: e.target.value })}
            />
          </Field>
          <Field label={t('website')}>
            <input
              className="input"
              disabled={!editable}
              value={cur.website ?? ''}
              onChange={(e) => setP({ ...cur, website: e.target.value })}
            />
          </Field>
          <Field label={t('description')}>
            <textarea
              className="textarea"
              rows={4}
              disabled={!editable}
              value={cur.description ?? ''}
              onChange={(e) => setP({ ...cur, description: e.target.value })}
            />
          </Field>
          <label className="flex items-center gap-3 text-sm">
            <input
              type="checkbox"
              disabled={!editable}
              checked={!!cur.autoApprove}
              onChange={(e) => setP({ ...cur, autoApprove: e.target.checked })}
            />{' '}
            {t('autoApprove')}
          </label>
          <label className="flex items-center gap-3 text-sm">
            <input
              type="checkbox"
              disabled={!editable}
              checked={!!cur.published}
              onChange={(e) => setP({ ...cur, published: e.target.checked })}
            />{' '}
            {t('published')}
          </label>
          <div className="flex items-center gap-3">
            <button type="submit" className="btn btn-primary" disabled={!p || save.isPending || !editable}>
              {t('save')}
            </button>
            {save.isSuccess && !p && <span className="mono text-xs text-ok">{t('saved')}</span>}
          </div>
          <ErrorNote error={save.error} />
          <div className="flex items-center gap-2 text-sm text-mute">
            {t('programPage')}:{' '}
            <span className="mono text-xs text-body">{`${location.origin}/join/${q.data.program.slug}`}</span>
            <CopyButton value={`${location.origin}/join/${q.data.program.slug}`} />
          </div>
        </form>
      </Card>
      <Card>
        <div className="mb-6 flex items-center justify-between">
          <Eyebrow>{t('rewards')}</Eyebrow>
          <span className="mono text-xs text-mute">
            {t('termsVersion')} v{q.data.program.termsVersion}
          </span>
        </div>
        <div className="grid gap-5">
          <div className="grid grid-cols-[1fr_1fr] gap-3">
            <Field label={t('rewards')}>
              <select
                className="select"
                disabled={!editable}
                value={`${reward.event}:${reward.type}`}
                onChange={(e) => {
                  const [event, type] = e.target.value.split(':') as ['sale' | 'lead', 'percent' | 'fixed']
                  setTerms({
                    ...tm,
                    rewards: [
                      type === 'percent'
                        ? { event, type, bps: reward.bps ?? 2000 }
                        : { event, type, amountMinor: reward.amountMinor ?? 1000 },
                    ],
                  })
                }}
              >
                <option value="sale:percent">{t('percentOfSale')}</option>
                <option value="sale:fixed">{t('sale')} · fixed</option>
                <option value="lead:fixed">{t('fixedPerLead')}</option>
              </select>
            </Field>
            <Field label={reward.type === 'percent' ? '%' : q.data.program.currency}>
              <input
                className="input"
                type="number"
                disabled={!editable}
                min={reward.type === 'percent' ? 0.01 : 0.01}
                step={0.01}
                value={reward.type === 'percent' ? (reward.bps ?? 0) / 100 : (reward.amountMinor ?? 0) / 100}
                onChange={(e) => {
                  const v = Math.round(Number(e.target.value) * 100)
                  setTerms({
                    ...tm,
                    rewards: [
                      reward.type === 'percent' ? { ...reward, bps: v } : { ...reward, amountMinor: v },
                    ],
                  })
                }}
              />
            </Field>
          </div>
          <div className="text-sm text-mute">
            {t('standardsNote', {
              months: q.data.standards.rewardDurationMonths,
              days: q.data.standards.cookieDays,
            })}
          </div>
          <details className="rounded-lg border border-hairline p-4">
            <summary className="cursor-pointer text-sm text-body">{t('advanced')}</summary>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <Field label={t('holdDays')}>
                <select
                  className="select"
                  disabled={!editable}
                  value={tm.holdDays}
                  onChange={(e) => setTerms({ ...tm, holdDays: Number(e.target.value) })}
                >
                  {[0, 7, 14, 30, 60, 90].map((d) => (
                    <option key={d} value={d}>
                      {d} {t('days')}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label={t('minPayout')}>
                <select
                  className="select"
                  disabled={!editable}
                  value={tm.minPayoutMinor}
                  onChange={(e) => setTerms({ ...tm, minPayoutMinor: Number(e.target.value) })}
                >
                  {[0, 1000, 2500, 5000, 10000].map((m) => (
                    <option key={m} value={m}>
                      {fmtMoney(m, q.data.program.currency, locale)}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <Field label="Allowed hosts" hint="acme.com, app.acme.com">
              <input
                className="input mt-2"
                disabled={!editable}
                value={tm.allowedHosts.join(', ')}
                onChange={(e) =>
                  setTerms({
                    ...tm,
                    allowedHosts: e.target.value
                      .split(',')
                      .map((s) => s.trim().toLowerCase())
                      .filter(Boolean),
                  })
                }
              />
            </Field>
          </details>
          <button
            type="button"
            className="btn btn-primary justify-self-start"
            disabled={!terms || saveTerms.isPending || !editable}
            onClick={() => saveTerms.mutate()}
          >
            {t('saveTerms')}
          </button>
          <ErrorNote error={saveTerms.error} />
        </div>
      </Card>
    </div>
  )
}

function IntegrationsTab() {
  const { t } = useT()
  const ws = useWs()
  const qc = useQueryClient()
  const q = useQuery({
    queryKey: ['integrations', ws],
    queryFn: () =>
      api<{
        connections: { provider: string; mode: string; meta: Record<string, string> }[]
        webhookUrls: Record<'test' | 'live', string>
        stripeEvents: string[]
      }>(wsPath(ws, '/integrations')),
  })
  const [stripe, setStripe] = useState({ mode: 'test', webhookSecret: '', restrictedKey: '' })
  const [paypal, setPaypal] = useState({ mode: 'test', clientId: '', clientSecret: '' })
  const inv = () => qc.invalidateQueries({ queryKey: ['integrations', ws] })
  const saveStripe = useMutation({
    mutationFn: () =>
      api(wsPath(ws, '/integrations/stripe'), {
        body: {
          mode: stripe.mode,
          webhookSecret: stripe.webhookSecret,
          ...(stripe.restrictedKey ? { restrictedKey: stripe.restrictedKey } : {}),
        },
      }),
    onSuccess: () => {
      inv()
      setStripe({ ...stripe, webhookSecret: '', restrictedKey: '' })
    },
  })
  const savePaypal = useMutation({
    mutationFn: () => api(wsPath(ws, '/integrations/paypal'), { body: paypal }),
    onSuccess: () => {
      inv()
      setPaypal({ ...paypal, clientId: '', clientSecret: '' })
    },
  })
  const remove = useMutation({
    mutationFn: (x: { provider: string; mode: string }) =>
      api(wsPath(ws, `/integrations/${x.provider}/${x.mode}`), { method: 'DELETE' }),
    onSuccess: inv,
  })
  if (!q.data) return <Spinner />
  const conns = q.data.connections
  const conn = (provider: string) => (
    <div className="mb-6 flex flex-wrap gap-2">
      {(['test', 'live'] as const).map((m) => {
        const c = conns.find((x) => x.provider === provider && x.mode === m)
        return (
          <span key={m} className={`badge ${c ? 'badge-ok' : ''}`}>
            {m}: {c ? t('connected') : t('notConnected')}
            {c && (
              <button
                type="button"
                className="ml-1 text-mute hover:text-ink"
                onClick={() => remove.mutate({ provider, mode: m })}
                aria-label={t('disconnect')}
              >
                ✕
              </button>
            )}
          </span>
        )
      })}
    </div>
  )
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <Eyebrow className="mb-3">{t('stripeTitle')}</Eyebrow>
        {conn('stripe')}
        <p className="mb-4 text-sm text-body">{t('stripeText')}</p>
        <Field label={`${t('webhookUrl')} (${stripe.mode})`}>
          <CodeBlock>{q.data.webhookUrls[stripe.mode as 'test']}</CodeBlock>
        </Field>
        <div className="mono my-4 text-xs text-mute">{q.data.stripeEvents.join(' · ')}</div>
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault()
            saveStripe.mutate()
          }}
        >
          <Field label={t('mode')}>
            <select
              className="select"
              value={stripe.mode}
              onChange={(e) => setStripe({ ...stripe, mode: e.target.value })}
            >
              <option value="test">{t('test')}</option>
              <option value="live">{t('live')}</option>
            </select>
          </Field>
          <Field label={t('webhookSecret')}>
            <input
              className="input mono"
              required
              autoComplete="off"
              value={stripe.webhookSecret}
              onChange={(e) => setStripe({ ...stripe, webhookSecret: e.target.value.trim() })}
            />
          </Field>
          <Field label={t('restrictedKey')}>
            <input
              className="input mono"
              autoComplete="off"
              value={stripe.restrictedKey}
              onChange={(e) => setStripe({ ...stripe, restrictedKey: e.target.value.trim() })}
            />
          </Field>
          <button
            type="submit"
            className="btn btn-primary justify-self-start"
            disabled={saveStripe.isPending}
          >
            {t('connect')}
          </button>
          <ErrorNote error={saveStripe.error} />
        </form>
      </Card>
      <Card>
        <Eyebrow className="mb-3">{t('paypalTitle')}</Eyebrow>
        {conn('paypal')}
        <p className="mb-6 text-sm text-body">{t('paypalText')}</p>
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault()
            savePaypal.mutate()
          }}
        >
          <Field label={t('mode')}>
            <select
              className="select"
              value={paypal.mode}
              onChange={(e) => setPaypal({ ...paypal, mode: e.target.value })}
            >
              <option value="test">Sandbox</option>
              <option value="live">{t('live')}</option>
            </select>
          </Field>
          <Field label={t('clientId')}>
            <input
              className="input mono"
              required
              autoComplete="off"
              value={paypal.clientId}
              onChange={(e) => setPaypal({ ...paypal, clientId: e.target.value.trim() })}
            />
          </Field>
          <Field label={t('clientSecret')}>
            <input
              className="input mono"
              type="password"
              required
              autoComplete="off"
              value={paypal.clientSecret}
              onChange={(e) => setPaypal({ ...paypal, clientSecret: e.target.value.trim() })}
            />
          </Field>
          <button
            type="submit"
            className="btn btn-primary justify-self-start"
            disabled={savePaypal.isPending}
          >
            {t('connect')}
          </button>
          <ErrorNote error={savePaypal.error} />
        </form>
        <div className="mt-8 border-t border-hairline pt-6 text-sm text-mute">
          USDT / USDC: {t('exportCsv')} → {t('markPaid')}
        </div>
      </Card>
    </div>
  )
}

function DomainsTab() {
  const { t } = useT()
  const ws = useWs()
  const qc = useQueryClient()
  const q = useQuery({
    queryKey: ['domains', ws],
    queryFn: () =>
      api<{
        domains: {
          id: string
          hostname: string
          kind: string
          status: string
          verifyToken: string
          error: string | null
        }[]
        cnameTarget: string
      }>(wsPath(ws, '/domains')),
  })
  const [f, setF] = useState({ hostname: '', kind: 'links' })
  const add = useMutation({
    mutationFn: () => api(wsPath(ws, '/domains'), { body: f }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['domains', ws] })
      setF({ ...f, hostname: '' })
    },
  })
  const del = useMutation({
    mutationFn: (id: string) => api(wsPath(ws, `/domains/${id}`), { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['domains', ws] }),
  })
  if (!q.data) return <Spinner />
  return (
    <div className="grid gap-4">
      <Card>
        <form
          className="grid items-end gap-3 md:grid-cols-[1fr_200px_auto]"
          onSubmit={(e) => {
            e.preventDefault()
            add.mutate()
          }}
        >
          <Field label={t('hostname')}>
            <input
              className="input"
              required
              placeholder="go.acme.com"
              value={f.hostname}
              onChange={(e) => setF({ ...f, hostname: e.target.value })}
            />
          </Field>
          <Field label={t('domainKind')}>
            <select className="select" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })}>
              <option value="links">{t('kindLinks')}</option>
              <option value="portal">{t('kindPortal')}</option>
            </select>
          </Field>
          <button type="submit" className="btn btn-primary" disabled={add.isPending}>
            {t('addDomain')}
          </button>
        </form>
        <ErrorNote error={add.error} />
      </Card>
      {q.data.domains.map((d) => (
        <Card key={d.id}>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="text-lg">{d.hostname}</span>
              <Status value={d.status} />
              <span className="badge">{d.kind === 'links' ? t('kindLinks') : t('kindPortal')}</span>
            </div>
            <button type="button" className="btn btn-sm btn-ghost" onClick={() => del.mutate(d.id)}>
              {t('remove')}
            </button>
          </div>
          {d.status !== 'active' && (
            <>
              <Eyebrow className="mb-3">{t('dnsRecords')}</Eyebrow>
              <table className="table">
                <tbody>
                  <tr>
                    <td className="mono text-xs">TXT</td>
                    <td className="mono text-xs">_refego-verify.{d.hostname}</td>
                    <td className="mono text-xs">{d.verifyToken}</td>
                    <td>
                      <CopyButton value={d.verifyToken} />
                    </td>
                  </tr>
                  <tr>
                    <td className="mono text-xs">CNAME</td>
                    <td className="mono text-xs">{d.hostname}</td>
                    <td className="mono text-xs">{q.data.cnameTarget}</td>
                    <td>
                      <CopyButton value={q.data.cnameTarget} />
                    </td>
                  </tr>
                </tbody>
              </table>
              {d.error && (
                <div className="mt-3 text-xs text-sunset">
                  {DOMAIN_ERRORS.includes(d.error as (typeof DOMAIN_ERRORS)[number])
                    ? t(`domainErr_${d.error as (typeof DOMAIN_ERRORS)[number]}`)
                    : d.error}
                </div>
              )}
            </>
          )}
        </Card>
      ))}
    </div>
  )
}

const SCOPES = [
  'programs.read',
  'partners.read',
  'partners.write',
  'commissions.read',
  'track.write',
] as const

function ApiKeysTab() {
  const { t, locale } = useT()
  const ws = useWs()
  const qc = useQueryClient()
  const q = useQuery({
    queryKey: ['keys', ws],
    queryFn: () =>
      api<{
        keys: {
          id: string
          name: string
          prefix: string
          scopes: string[]
          mode: string
          lastUsedAt: string | null
          revokedAt: string | null
          createdAt: string
        }[]
      }>(wsPath(ws, '/api-keys')),
  })
  const [f, setF] = useState({
    name: '',
    mode: 'test',
    scopes: ['track.write', 'commissions.read'] as string[],
  })
  const [secret, setSecret] = useState<string | null>(null)
  const create = useMutation({
    mutationFn: () => api<{ secret: string }>(wsPath(ws, '/api-keys'), { body: f }),
    onSuccess: (r) => {
      setSecret(r.secret)
      qc.invalidateQueries({ queryKey: ['keys', ws] })
    },
  })
  const revoke = useMutation({
    mutationFn: (id: string) => api(wsPath(ws, `/api-keys/${id}`), { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['keys', ws] }),
  })
  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_1.4fr]">
      <Card>
        <Eyebrow className="mb-6">{t('newKey')}</Eyebrow>
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault()
            create.mutate()
          }}
        >
          <Field label={t('name')}>
            <input
              className="input"
              required
              value={f.name}
              onChange={(e) => setF({ ...f, name: e.target.value })}
              placeholder="Backend"
            />
          </Field>
          <Field label={t('mode')}>
            <select className="select" value={f.mode} onChange={(e) => setF({ ...f, mode: e.target.value })}>
              <option value="test">rg_test_</option>
              <option value="live">rg_live_</option>
            </select>
          </Field>
          <div>
            <span className="label">{t('scopes')}</span>
            <div className="grid gap-2">
              {SCOPES.map((s) => (
                <label key={s} className="mono flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={f.scopes.includes(s)}
                    onChange={(e) =>
                      setF({
                        ...f,
                        scopes: e.target.checked ? [...f.scopes, s] : f.scopes.filter((x) => x !== s),
                      })
                    }
                  />
                  {s}
                </label>
              ))}
            </div>
          </div>
          <button type="submit" className="btn btn-primary justify-self-start" disabled={create.isPending}>
            {t('newKey')}
          </button>
          <ErrorNote error={create.error} />
          {secret && (
            <div>
              <p className="mb-2 text-sm text-sunset">{t('keyOnce')}</p>
              <CodeBlock>{secret}</CodeBlock>
            </div>
          )}
        </form>
      </Card>
      <Card pad={false}>
        <table className="table">
          <thead>
            <tr>
              <th>{t('name')}</th>
              <th>{t('scopes')}</th>
              <th>{t('lastUsed')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(q.data?.keys ?? []).map((k) => (
              <tr key={k.id}>
                <td>
                  <div className="text-ink">{k.name}</div>
                  <div className="mono text-xs text-mute">{k.prefix}…</div>
                </td>
                <td className="mono text-[11px]">{k.scopes.join(', ')}</td>
                <td className="mono text-xs">{fmtDate(k.lastUsedAt, locale)}</td>
                <td className="text-right">
                  {k.revokedAt ? (
                    <span className="badge">{t('revoked')}</span>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-sm btn-ghost"
                      onClick={() => revoke.mutate(k.id)}
                    >
                      {t('revoke')}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  )
}

function TeamTab() {
  const { t, locale } = useT()
  const ws = useWs()
  const qc = useQueryClient()
  const q = useQuery({
    queryKey: ['team', ws],
    queryFn: () =>
      api<{
        members: {
          id: string
          role: string
          name: string
          email: string
          userId: string
          createdAt: string
        }[]
        me: string
        role: string
      }>(wsPath(ws, '/team')),
  })
  const [f, setF] = useState({ email: '', role: 'admin' })
  const invite = useMutation({
    mutationFn: () => api(wsPath(ws, '/team/invite'), { body: f }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['team', ws] })
      setF({ ...f, email: '' })
    },
  })
  const remove = useMutation({
    mutationFn: (id: string) => api(wsPath(ws, `/team/${id}`), { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team', ws] }),
  })
  if (!q.data) return <Spinner />
  const isOwner = q.data.role === 'owner'
  return (
    <div className="grid gap-4">
      {isOwner && (
        <Card>
          <form
            className="grid items-end gap-3 md:grid-cols-[1fr_200px_auto]"
            onSubmit={(e) => {
              e.preventDefault()
              invite.mutate()
            }}
          >
            <Field label={t('email')}>
              <input
                className="input"
                type="email"
                required
                value={f.email}
                onChange={(e) => setF({ ...f, email: e.target.value })}
              />
            </Field>
            <Field label={t('role')}>
              <select
                className="select"
                value={f.role}
                onChange={(e) => setF({ ...f, role: e.target.value })}
              >
                <option value="admin">admin</option>
                <option value="finance">finance</option>
                <option value="viewer">viewer</option>
              </select>
            </Field>
            <button type="submit" className="btn btn-primary" disabled={invite.isPending}>
              {t('inviteMember')}
            </button>
          </form>
          <ErrorNote error={invite.error} />
        </Card>
      )}
      <Card pad={false}>
        <table className="table">
          <tbody>
            {q.data.members.map((m) => (
              <tr key={m.id}>
                <td>
                  <div className="text-ink">{m.name || m.email}</div>
                  <div className="text-xs text-mute">{m.email}</div>
                </td>
                <td>
                  <span className="badge">{m.role}</span>
                </td>
                <td className="mono text-xs">{fmtDate(m.createdAt, locale)}</td>
                <td className="text-right">
                  {isOwner && m.userId !== q.data.me && (
                    <button
                      type="button"
                      className="btn btn-sm btn-ghost"
                      onClick={() => remove.mutate(m.id)}
                    >
                      {t('remove')}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  )
}

function BillingTab() {
  const { t, locale } = useT()
  const ws = useWs()
  const role = useRole()
  const cardResult = new URLSearchParams(location.search).get('card')
  const addCard = useMutation({
    mutationFn: () => api<{ url: string }>(wsPath(ws, '/billing/card'), { method: 'POST' }),
    onSuccess: (r) => {
      location.href = r.url
    },
  })
  const q = useQuery({
    queryKey: ['billing', ws],
    queryFn: () =>
      api<{
        month: string
        volumeMinor: number
        feeMinor: number
        invoices: {
          id: string
          month: string
          volumeMinor: number
          feeMinor: number
          status: string
          dueAt: string | null
          hostedUrl: string | null
        }[]
        card: string | null
        stripeBilling: boolean
      }>(wsPath(ws, '/billing')),
  })
  if (!q.data) return <Spinner />
  return (
    <div className="grid gap-4">
      <div className="grid gap-3 md:grid-cols-2">
        <Kpi
          label={`${t('payoutVolume')} · ${q.data.month}`}
          value={fmtMoney(q.data.volumeMinor, 'USD', locale)}
        />
        <Kpi
          label={t('feeThisMonth')}
          value={fmtMoney(q.data.feeMinor, 'USD', locale)}
          sub={q.data.feeMinor === 0 ? 'FREE' : undefined}
          tone="ok"
        />
      </div>
      {q.data.stripeBilling && (
        <Card>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <Eyebrow className="mb-2">{t('billingCard')}</Eyebrow>
              <div className="text-body">{q.data.card ?? t('noCard')}</div>
              {cardResult === 'saved' && !q.data.card && (
                <div className="mt-1 text-sm text-mute">{t('cardProcessing')}</div>
              )}
            </div>
            {role === 'owner' && (
              <button
                type="button"
                className="btn"
                disabled={addCard.isPending}
                onClick={() => addCard.mutate()}
              >
                {q.data.card ? t('replaceCard') : t('addCard')}
              </button>
            )}
          </div>
          <ErrorNote error={addCard.error} />
        </Card>
      )}
      <Card>
        <Eyebrow className="mb-3">{t('pricingTitle')}</Eyebrow>
        <p className="text-body">{t('pricingText')}</p>
      </Card>
      {!!q.data.invoices.length && (
        <Card pad={false}>
          <table className="table">
            <thead>
              <tr>
                <th>{t('month')}</th>
                <th>{t('payoutVolume')}</th>
                <th>{t('fee')}</th>
                <th>{t('dueDate')}</th>
                <th>{t('status')}</th>
              </tr>
            </thead>
            <tbody>
              {q.data.invoices.map((i) => (
                <tr key={i.id}>
                  <td className="mono">{i.month}</td>
                  <td className="mono">{fmtMoney(i.volumeMinor, 'USD', locale)}</td>
                  <td className="mono">{fmtMoney(i.feeMinor, 'USD', locale)}</td>
                  <td className="mono text-xs">{i.status === 'open' ? fmtDate(i.dueAt, locale) : '—'}</td>
                  <td>
                    <span className="badge">{i.status}</span>
                    {i.status === 'open' && i.hostedUrl && (
                      <a
                        className="btn btn-sm ml-2"
                        href={i.hostedUrl}
                        target="_blank"
                        rel="noreferrer noopener"
                      >
                        {t('payInvoice')} ↗
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  )
}

function AuditTab() {
  const { locale } = useT()
  const ws = useWs()
  const q = useQuery({
    queryKey: ['audit', ws],
    queryFn: () =>
      api<{
        entries: {
          id: number
          actorType: string
          actorId: string | null
          action: string
          targetType: string | null
          targetId: string | null
          createdAt: string
        }[]
      }>(wsPath(ws, '/audit')),
  })
  if (!q.data) return <Spinner />
  return (
    <Card pad={false}>
      <div className="overflow-x-auto">
        <table className="table">
          <tbody>
            {q.data.entries.map((e) => (
              <tr key={e.id}>
                <td className="mono whitespace-nowrap text-xs">{fmtDate(e.createdAt, locale, true)}</td>
                <td className="mono text-xs">{e.action}</td>
                <td className="mono text-xs text-mute">
                  {e.actorType}
                  {e.actorId ? ` · ${e.actorId.slice(0, 12)}…` : ''}
                </td>
                <td className="mono text-xs text-mute">{e.targetId ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  )
}

interface Endpoint {
  id: string
  url: string
  events: string[]
  active: boolean
  createdAt: string
}
interface Delivery {
  id: number
  endpoint_id: string
  type: string | null
  status: string
  attempts: number
  last_error: string | null
  created_at: string
}

function WebhooksTab() {
  const { t, locale } = useT()
  const ws = useWs()
  const qc = useQueryClient()
  const q = useQuery({
    queryKey: ['webhooks', ws],
    queryFn: () =>
      api<{ endpoints: Endpoint[]; deliveries: Delivery[]; events: string[] }>(wsPath(ws, '/webhooks')),
    refetchInterval: 15_000,
  })
  const [url, setUrl] = useState('')
  const [events, setEvents] = useState<string[]>(['*'])
  const [secret, setSecret] = useState<string | null>(null)
  const inv = () => qc.invalidateQueries({ queryKey: ['webhooks', ws] })
  const create = useMutation({
    mutationFn: () => api<{ secret: string }>(wsPath(ws, '/webhooks'), { body: { url, events } }),
    onSuccess: (r) => {
      setSecret(r.secret)
      setUrl('')
      inv()
    },
  })
  const remove = useMutation({
    mutationFn: (id: string) => api(wsPath(ws, `/webhooks/${id}`), { method: 'DELETE' }),
    onSuccess: inv,
  })
  const test = useMutation({
    mutationFn: (id: string) => api(wsPath(ws, `/webhooks/${id}/test`), { method: 'POST' }),
    onSuccess: inv,
  })
  const retry = useMutation({
    mutationFn: (id: number) => api(wsPath(ws, `/webhooks/deliveries/${id}/retry`), { method: 'POST' }),
    onSuccess: inv,
  })
  if (!q.data) return <Spinner />
  const toggle = (e: string) =>
    setEvents((cur) =>
      e === '*' ? ['*'] : cur.includes(e) ? cur.filter((x) => x !== e) : [...cur.filter((x) => x !== '*'), e],
    )
  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_1.4fr]">
      <Card>
        <Eyebrow className="mb-6">{t('addEndpoint')}</Eyebrow>
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault()
            create.mutate()
          }}
        >
          <Field label={t('endpointUrl')}>
            <input
              className="input mono"
              type="url"
              required
              placeholder="https://example.com/refego"
              value={url}
              onChange={(e) => setUrl(e.target.value.trim())}
            />
          </Field>
          <div>
            <span className="label">{t('events')}</span>
            <div className="grid gap-2">
              {['*', ...q.data.events].map((e) => (
                <label key={e} className="mono flex items-center gap-2 text-xs">
                  <input type="checkbox" checked={events.includes(e)} onChange={() => toggle(e)} />
                  {e === '*' ? t('allEvents') : e}
                </label>
              ))}
            </div>
          </div>
          <button
            type="submit"
            className="btn btn-primary justify-self-start"
            disabled={create.isPending || !events.length}
          >
            {t('addEndpoint')}
          </button>
          <ErrorNote error={create.error} />
          {secret && (
            <div>
              <p className="mb-2 text-sm text-sunset">{t('secretOnce')}</p>
              <CodeBlock>{secret}</CodeBlock>
            </div>
          )}
        </form>
      </Card>
      <div className="grid content-start gap-4">
        {!q.data.endpoints.length && (
          <Card>
            <p className="text-sm text-mute">{t('noEndpoints')}</p>
          </Card>
        )}
        {q.data.endpoints.map((ep) => (
          <Card key={ep.id}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span className="mono break-all text-sm text-ink">{ep.url}</span>
              <div className="flex gap-2">
                <button type="button" className="btn btn-sm" onClick={() => test.mutate(ep.id)}>
                  {t('sendTest')}
                </button>
                <button type="button" className="btn btn-sm btn-ghost" onClick={() => remove.mutate(ep.id)}>
                  {t('remove')}
                </button>
              </div>
            </div>
            <div className="mono mt-3 text-[11px] text-mute">{ep.events.join(' · ')}</div>
          </Card>
        ))}
        {!!q.data.deliveries.length && (
          <Card pad={false}>
            <div className="px-4 pt-4">
              <Eyebrow>{t('deliveries')}</Eyebrow>
            </div>
            <div className="overflow-x-auto">
              <table className="table">
                <tbody>
                  {q.data.deliveries.map((d) => (
                    <tr key={d.id}>
                      <td className="mono whitespace-nowrap text-xs">
                        {fmtDate(d.created_at, locale, true)}
                      </td>
                      <td className="mono text-xs">{d.type ?? '—'}</td>
                      <td>
                        <span
                          className={`badge ${d.status === 'done' ? 'badge-ok' : d.status === 'dead' ? 'badge-bad' : 'badge-warn'}`}
                        >
                          {d.status}
                        </span>
                        {d.last_error && (
                          <div
                            className="mono mt-1 max-w-[220px] truncate text-[11px] text-danger"
                            title={d.last_error}
                          >
                            {d.last_error}
                          </div>
                        )}
                      </td>
                      <td className="mono text-xs">×{d.attempts}</td>
                      <td className="text-right">
                        {d.status === 'dead' && (
                          <button type="button" className="btn btn-sm" onClick={() => retry.mutate(d.id)}>
                            {t('retry')}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>
    </div>
  )
}

interface CouponRow {
  id: string
  code: string
  stripe_promotion_code_id: string | null
  override_bps: number | null
  partner_name: string
  partner_email: string
  customers: number
}

function CouponsTab() {
  const { t } = useT()
  const ws = useWs()
  const role = useRole()
  const qc = useQueryClient()
  const q = useQuery({
    queryKey: ['coupons', ws],
    queryFn: () => api<{ coupons: CouponRow[] }>(wsPath(ws, '/coupons')),
  })
  const partners = useQuery({
    queryKey: ['partners', ws, 'approved'],
    queryFn: () =>
      api<{ partners: { partner_id: string; name: string; email: string }[] }>(
        wsPath(ws, '/partners?status=approved'),
      ),
  })
  const [f, setF] = useState({ partnerId: '', code: '', promo: '', rate: '' })
  const inv = () => qc.invalidateQueries({ queryKey: ['coupons', ws] })
  const create = useMutation({
    mutationFn: () =>
      api(wsPath(ws, '/coupons'), {
        body: {
          partnerId: f.partnerId || partners.data?.partners[0]?.partner_id,
          code: f.code,
          ...(f.promo ? { stripePromotionCodeId: f.promo } : {}),
          ...(f.rate ? { overrideBps: Math.round(Number(f.rate) * 100) } : {}),
        },
      }),
    onSuccess: () => {
      setF({ ...f, code: '', promo: '', rate: '' })
      inv()
    },
  })
  const remove = useMutation({
    mutationFn: (id: string) => api(wsPath(ws, `/coupons/${id}`), { method: 'DELETE' }),
    onSuccess: inv,
  })
  const editable = role === 'owner' || role === 'admin'
  const list = partners.data?.partners ?? []
  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_1.4fr]">
      <Card>
        <Eyebrow className="mb-3">{t('addCoupon')}</Eyebrow>
        <p className="mb-6 text-sm text-body">{t('couponsHint')}</p>
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault()
            create.mutate()
          }}
        >
          <Field label={t('partner')}>
            <select
              className="select"
              value={f.partnerId}
              onChange={(e) => setF({ ...f, partnerId: e.target.value })}
              disabled={!list.length || !editable}
            >
              {list.map((p) => (
                <option key={p.partner_id} value={p.partner_id}>
                  {p.name} · {p.email}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('couponCode')}>
            <input
              className="input mono uppercase"
              required
              pattern="[A-Za-z0-9][A-Za-z0-9_\-]{2,31}"
              value={f.code}
              onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })}
              placeholder="JANE20"
              disabled={!editable}
            />
          </Field>
          <Field label={t('stripePromo')}>
            <input
              className="input mono"
              value={f.promo}
              onChange={(e) => setF({ ...f, promo: e.target.value.trim() })}
              placeholder="promo_…"
              disabled={!editable}
            />
          </Field>
          <Field label={t('rateOverride')}>
            <input
              className="input"
              type="number"
              min={0.01}
              max={100}
              step={0.01}
              value={f.rate}
              onChange={(e) => setF({ ...f, rate: e.target.value })}
              disabled={!editable}
            />
          </Field>
          <button
            type="submit"
            className="btn btn-primary justify-self-start"
            disabled={create.isPending || !list.length || !editable}
          >
            {t('addCoupon')}
          </button>
          <ErrorNote error={create.error} />
        </form>
      </Card>
      <Card pad={false}>
        <table className="table">
          <thead>
            <tr>
              <th>{t('couponCode')}</th>
              <th>{t('partner')}</th>
              <th>{t('customers')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(q.data?.coupons ?? []).map((k) => (
              <tr key={k.id}>
                <td>
                  <div className="mono text-ink">{k.code}</div>
                  <div className="mono text-[11px] text-mute">
                    {k.stripe_promotion_code_id ?? '—'}
                    {k.override_bps ? ` · ${k.override_bps / 100}%` : ''}
                  </div>
                </td>
                <td>
                  <div className="text-ink">{k.partner_name}</div>
                  <div className="text-xs text-mute">{k.partner_email}</div>
                </td>
                <td className="mono">{k.customers}</td>
                <td className="text-right">
                  {editable && (
                    <button
                      type="button"
                      className="btn btn-sm btn-ghost"
                      onClick={() => remove.mutate(k.id)}
                    >
                      {t('remove')}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  )
}
